/**
 * Pure rules behind "Send by Netlise" (README E) and the editor's "Issue and
 * send": which channel rows are usable and why not, what a send costs, the
 * cost line, the SendRequest, the idempotency key's lifetime and how a failed
 * send is explained. Template rendering mirrors lambda/invoices/templates.mjs
 * (templateFor, fill, fmtMoney, fmtDate) so the pre-issue text matches what
 * the server will send.
 */
import type { Channel, Credits, SendRequest, SendResult } from '../../internals/repository/InvoiceRepository';
import { isEmail, isSriLankaLandline, toE164 } from './shareLinks';

export type { Channel };
export type Lang = 'en' | 'si' | 'ta';
export type Reason = 'no_email' | 'no_phone' | 'landline' | 'not_configured' | 'opted_out';

export const CHANNELS: Channel[] = ['WHATSAPP', 'SMS', 'EMAIL'];
export const CHANNEL_META: Record<Channel, { name: string; glyph: string; bg: string }> = {
  WHATSAPP: { name: 'WhatsApp', glyph: 'W', bg: '#128C5A' },
  SMS: { name: 'SMS', glyph: 'S', bg: '#0C8FD4' },
  EMAIL: { name: 'Email', glyph: 'E', bg: '#4E45E4' },
};
const IN_SENTENCE: Record<Channel, string> = { WHATSAPP: 'WhatsApp', SMS: 'SMS', EMAIL: 'email' };
const DEFAULT_COSTS: Credits['costs'] = { EMAIL: 0, WHATSAPP: 1, SMS_SEGMENT: 1 };
const usesPhone = (c: Channel) => c !== 'EMAIL';

export const TEMPLATE_OPTIONS = [
  { value: 'invoice_issued', label: 'Invoice issued' },
  { value: 'invoice_reminder', label: 'Payment reminder' },
  { value: 'invoice_overdue', label: 'Overdue notice' },
  { value: 'payment_received', label: 'Payment received' },
  { value: 'quotation_sent', label: 'Quotation' },
];

// ── Availability ───────────────────────────────────────────────────────────

export function reasonText(channel: Channel, reason: Reason): string {
  switch (reason) {
    case 'no_email': return 'No email address on file';
    case 'no_phone': return 'No phone number on file';
    case 'landline': return 'Landline only, no mobile number';
    case 'not_configured': return 'Not set up yet — share from your device instead';
    case 'opted_out': return `Customer opted out of ${IN_SENTENCE[channel]}`;
    default: return 'Not available';
  }
}

export interface Availability { available: boolean; reason?: Reason; text?: string }

/**
 * A row is usable unless the server said no (422), Netlise cannot send on it
 * yet (credits.channels), or the customer has no usable contact on file.
 * `configured` null means unknown (credits did not load): the server decides.
 */
export function channelAvailability(input: {
  customer: { phone?: string | null; email?: string | null };
  configured?: Partial<Record<Channel, boolean>> | null;
  server?: Partial<Record<Channel, Reason>>;
}): Record<Channel, Availability> {
  const phone = toE164(input.customer.phone);
  const hasEmail = !!input.customer.email?.trim();
  const out = {} as Record<Channel, Availability>;
  CHANNELS.forEach((ch) => {
    let reason: Reason | undefined = input.server?.[ch];
    if (!reason && input.configured && input.configured[ch] === false) reason = 'not_configured';
    if (!reason && ch === 'EMAIL' && !hasEmail) reason = 'no_email';
    if (!reason && usesPhone(ch) && !phone) reason = 'no_phone';
    if (!reason && usesPhone(ch) && isSriLankaLandline(phone)) reason = 'landline';
    out[ch] = reason ? { available: false, reason, text: reasonText(ch, reason) } : { available: true };
  });
  return out;
}

/** settings.defaultChannels ∩ available; if that leaves nothing, the cheapest usable one. */
export function defaultTicks(defaults: Channel[] | undefined | null, avail: Record<Channel, Availability>): Channel[] {
  const want = defaults && defaults.length ? defaults : ['WHATSAPP', 'EMAIL'] as Channel[];
  const picked = CHANNELS.filter((c) => want.includes(c) && avail[c].available);
  if (picked.length) return picked;
  const fallback = (['EMAIL', 'WHATSAPP', 'SMS'] as Channel[]).find((c) => avail[c].available);
  return fallback ? [fallback] : [];
}

/** Ticked rows that are still usable, in display order. */
export const usableTicks = (ticked: Channel[], avail: Record<Channel, Availability>) =>
  CHANNELS.filter((c) => ticked.includes(c) && avail[c].available);

export interface RecipientProblems { phone?: string; email?: string }

/** The edited recipients, checked only for the channels that need them. */
export function recipientProblems(to: { phone?: string; email?: string }, ticked: Channel[]): RecipientProblems {
  const p: RecipientProblems = {};
  if (ticked.some(usesPhone)) {
    const raw = (to.phone || '').trim();
    const e164 = toE164(raw);
    if (!raw) p.phone = 'Enter a mobile number';
    else if (!e164) p.phone = 'Not a valid mobile number';
    else if (isSriLankaLandline(e164)) p.phone = 'This is a landline. Enter a mobile number.';
  }
  if (ticked.includes('EMAIL')) {
    const raw = (to.email || '').trim();
    if (!raw) p.email = 'Enter an email address';
    else if (!isEmail(raw)) p.email = 'Not a valid email address';
  }
  return p;
}

// ── Cost ───────────────────────────────────────────────────────────────────

const credits = (n: number) => `${n} ${n === 1 ? 'credit' : 'credits'}`;

export function channelCost(ch: Channel, smsSegments: number, costs?: Credits['costs'] | null): number {
  const c = costs || DEFAULT_COSTS;
  if (ch === 'EMAIL') return c.EMAIL;
  if (ch === 'WHATSAPP') return c.WHATSAPP;
  return Math.max(1, smsSegments) * c.SMS_SEGMENT;
}

/** "1 credit", "2 credits", "Free". */
export function channelCostLabel(ch: Channel, smsSegments: number, costs?: Credits['costs'] | null): string {
  const n = channelCost(ch, smsSegments, costs);
  return n === 0 ? 'Free' : credits(n);
}

export const totalCost = (ticked: Channel[], smsSegments: number, costs?: Credits['costs'] | null) =>
  ticked.reduce((sum, ch) => sum + channelCost(ch, smsSegments, costs), 0);

export interface CostLine {
  text: string;
  total: number;
  /** Not enough credits: Send is disabled. */
  short: boolean;
  none: boolean;
}

/**
 * "1 SMS segment + 1 WhatsApp message + email free = 2 credits · 145 left",
 * or "Not enough credits: this needs 3 and you have 2." when short.
 * `available` null (credits did not load) leaves out the balance.
 */
export function costLine(ticked: Channel[], smsSegments: number, costs: Credits['costs'] | null | undefined, available: number | null | undefined): CostLine {
  const total = totalCost(ticked, smsSegments, costs);
  if (!ticked.length) return { text: 'Choose at least one channel.', total: 0, short: false, none: true };
  const known = typeof available === 'number';
  if (known && total > (available as number)) {
    return { text: `Not enough credits: this needs ${total} and you have ${available}.`, total, short: true, none: false };
  }
  const parts: string[] = [];
  const segs = Math.max(1, smsSegments);
  if (ticked.includes('SMS')) parts.push(`${segs} SMS ${segs === 1 ? 'segment' : 'segments'}`);
  if (ticked.includes('WHATSAPP')) parts.push('1 WhatsApp message');
  if (ticked.includes('EMAIL')) parts.push(channelCost('EMAIL', segs, costs) === 0 ? 'email free' : `email ${credits(channelCost('EMAIL', segs, costs))}`);
  const left = known ? ` · ${(available as number) - total} left` : '';
  return { text: `${parts.join(' + ')} = ${credits(total)}${left}`, total, short: false, none: false };
}

// ── Request and idempotency ────────────────────────────────────────────────

export function buildSendRequest(input: {
  ticked: Channel[];
  to: { phone?: string; email?: string };
  templateKey?: string | null;
  language?: Lang | null;
  text: string;
  /** The template text the textarea started from; customText is sent only when it differs. */
  baseText: string;
  attachPdf: boolean;
  /** Pre-issue: shown values to turn back into {variables} (e.g. the link placeholder). */
  variables?: Record<string, string>;
}): SendRequest {
  const channels = CHANNELS.filter((c) => input.ticked.includes(c));
  const to: SendRequest['to'] = {};
  if (channels.some(usesPhone)) to.phone = toE164(input.to.phone) || (input.to.phone || '').trim();
  if (channels.includes('EMAIL')) to.email = (input.to.email || '').trim();
  const req: SendRequest = { channels, to };
  if (input.templateKey) req.templateKey = input.templateKey;
  if (input.language) req.language = input.language;
  const edited = input.text !== input.baseText;
  // WhatsApp ignores customText (Meta-approved templates).
  if (edited && input.text.trim() && channels.some((c) => c !== 'WHATSAPP')) {
    req.customText = input.variables ? unfill(input.text, input.variables) : input.text;
  }
  if (channels.includes('EMAIL')) req.attachPdf = input.attachPdf;
  return req;
}

export function requestSignature(req: SendRequest): string {
  return JSON.stringify([
    [...req.channels].sort(), req.to.phone ?? '', req.to.email ?? '', req.templateKey ?? '', req.language ?? '',
    req.customText ?? null, req.attachPdf ?? null,
  ]);
}

export interface IdemState { key: string; sig: string }

/** Same content → same key (a retry or a double click never sends twice); changed content → a new key. */
export function idempotencyFor(prev: IdemState | null, req: SendRequest, make: () => string): IdemState {
  const sig = requestSignature(req);
  return prev && prev.sig === sig ? prev : { key: make(), sig };
}

// ── Results and failures ───────────────────────────────────────────────────

function joinList(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** "WhatsApp and email" from the messages the server queued. */
export function channelList(channels: Channel[]): string {
  return joinList(CHANNELS.filter((c) => channels.includes(c)).map((c) => IN_SENTENCE[c]));
}

/** "Sent by WhatsApp and email · 1 credit used". */
export function sentToast(result: Pick<SendResult, 'messages' | 'creditsUsed'>, fallback: Channel[] = [], prefix = 'Sent by'): string {
  const chans = result.messages?.length ? result.messages.map((m) => m.channel) : fallback;
  const used = result.creditsUsed ? ` · ${credits(result.creditsUsed)} used` : '';
  return `${prefix} ${channelList(chans)}${used}`;
}

export interface SendFailure {
  title: string;
  text: string;
  /** Network or server trouble: the button becomes "Try again" with the same key. */
  retry: boolean;
  server?: Partial<Record<Channel, Reason>>;
  recipient?: RecipientProblems;
  available?: number;
}

const CHANNEL_REASONS: Reason[] = ['no_email', 'no_phone', 'landline', 'not_configured', 'opted_out'];

export function sendFailure(e: any, opts: { preIssue?: boolean } = {}): SendFailure {
  const status = typeof e?.status === 'number' ? e.status : 0;
  const code = e?.code as string | undefined;
  const msg: string = e?.message || '';
  const details = e?.details || {};
  if (status === 0 || status >= 500 || code === 'unavailable' || code === 'network') {
    const why = status === 0 && msg ? msg : 'The sending service did not respond.';
    return opts.preIssue
      ? { title: 'The invoice could not be issued and sent', text: `${why} Try again.`, retry: true }
      : { title: 'The message could not be sent', text: `${why} No credits were used. Try again, or share from your device.`, retry: true };
  }
  const title = 'The message was not sent';
  if (code === 'insufficient_credits') {
    const out: SendFailure = { title: 'Not enough credits', text: msg || 'This send needs more credits than you have.', retry: false };
    if (typeof details.availableCredits === 'number') out.available = details.availableCredits;
    return out;
  }
  if (code === 'channel_unavailable') {
    const ch = details.channel as Channel | undefined;
    const reason = details.reason as string | undefined;
    const out: SendFailure = { title, text: msg || 'A channel is not available for this customer.', retry: false };
    if (reason === 'invalid_email') out.recipient = { email: 'Not a valid email address' };
    else if (ch && CHANNELS.includes(ch) && reason && (CHANNEL_REASONS as string[]).includes(reason)) {
      out.server = { [ch]: reason as Reason };
    }
    return out;
  }
  if (code === 'invalid_phone') return { title, text: msg || 'The mobile number is not valid.', retry: false, recipient: { phone: 'Not a valid mobile number' } };
  if (code === 'invalid_email') return { title, text: msg || 'The email address is not valid.', retry: false, recipient: { email: 'Not a valid email address' } };
  if (code === 'invalid_state') return { title, text: 'Only an issued document can be sent. Reload to see its current state.', retry: false };
  if (code === 'forbidden_role' || status === 403) return { title, text: 'Ask the shop owner or an admin to do this.', retry: false };
  return { title, text: msg || 'Something went wrong. Try again.', retry: false };
}

// ── Pre-issue templates (mirror of lambda/invoices/templates.mjs) ──────────

export const LINK_PLACEHOLDER = 'i.netlise.com/…';

type TemplateValue = string | { subject?: string; text: string };
type TemplateTree = Record<string, Record<string, Record<string, TemplateValue | undefined> | undefined> | undefined>;

const ALIASES: Record<string, string> = {
  customer_name: 'customerName', customer_first_name: 'customerFirstName', shop_name: 'shopName',
  invoice_number: 'number', document_number: 'number', due_date: 'dueDate', valid_until: 'validUntil',
  shop_phone: 'shopPhone', document_type: 'documentType',
};

export function fillTemplate(text: string, vars: Record<string, string>): string {
  return String(text).replace(/\{([a-zA-Z_]+)\}/g, (whole, key) => {
    const k = ALIASES[key] || key;
    return Object.prototype.hasOwnProperty.call(vars, k) ? vars[k] : whole;
  });
}

/**
 * The reverse of fillTemplate for the values that are not final before issue
 * (number, link) or that the server owns (amounts, dates), so an edited
 * pre-issue message still carries the real figures once sent. Longest
 * values first, so "Rs. 1,000.00" is not caught inside "Rs. 11,000.00".
 */
export function unfill(text: string, vars: Record<string, string>): string {
  const entries = Object.entries(vars).filter(([, v]) => v && v.length >= 3).sort((a, b) => b[1].length - a[1].length);
  let out = text;
  entries.forEach(([k, v]) => { out = out.split(v).join(`{${k}}`); });
  return out;
}

/** The shop's text first, then the built-in one, falling back to English and then to WhatsApp (as the server does). */
export function templateFor(
  settings: { templates?: unknown; templateDefaults?: unknown } | null | undefined,
  key: string,
  channel: Channel,
  lang: Lang,
): { subject?: string; text: string } {
  const shop = ((settings?.templates || {}) as TemplateTree)[key] || {};
  const all = (settings?.templateDefaults || {}) as TemplateTree;
  const defaults = all[key] || all.invoice_issued || {};
  const candidates: (TemplateValue | undefined)[] = [
    shop[channel]?.[lang], defaults[channel]?.[lang],
    shop.WHATSAPP?.[lang], defaults.WHATSAPP?.[lang],
    shop[channel]?.en, defaults[channel]?.en, defaults.WHATSAPP?.en,
  ];
  const t = candidates.find((c) => c && (typeof c === 'string' ? c.trim() : c.text));
  if (!t) return { text: '' };
  return typeof t === 'string' ? { text: t } : t;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const fmtDate = (iso?: string | null) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
};
export const fmtMoney = (minor: number, lang: Lang = 'en') => {
  const m = Math.round(minor || 0);
  const whole = Math.floor(Math.abs(m) / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const prefix = lang === 'si' ? 'රු.' : lang === 'ta' ? 'ரூ.' : 'Rs.';
  return `${m < 0 ? '−' : ''}${prefix} ${whole}.${String(Math.abs(m) % 100).padStart(2, '0')}`;
};

/** "INV-{YYYY}-{#####}" → "INV-2026-…": the number exists only after issue. */
export function numberPlaceholder(format: string | undefined | null, year: string): string {
  if (!format) return '…';
  const head = format.split(/\{#+\}/)[0].replace(/\{YYYY\}/g, year).replace(/\{YY\}/g, year.slice(2));
  return `${head.replace(/\{[^}]*\}/g, '')}…`;
}

/** "INV-{YYYY}-{#####}" → "INV-2026-00000": a stand-in as long as the real number. */
export function numberSample(format: string | undefined | null, year: string): string {
  if (!format) return 'INV-0000-00000';
  return format.replace(/\{YYYY\}/g, year).replace(/\{YY\}/g, year.slice(2)).replace(/\{(#+)\}/g, (_, h: string) => '0'.repeat(h.length)).replace(/\{[^}]*\}/g, '');
}

/**
 * The text as it will be sent, for counting SMS segments before issue: the
 * placeholders ("…" is not GSM-7) swapped for stand-ins of the real length.
 */
export function measurable(text: string, placeholders: { number: string; numberSample: string }): string {
  return text
    .split(LINK_PLACEHOLDER).join('https://i.netlise.com/xxxxxxxxxxxx')
    .split(placeholders.number).join(placeholders.numberSample);
}

export function preIssueVariables(input: {
  docType: 'INVOICE' | 'QUOTATION';
  customerName: string;
  shopName: string;
  shopPhone?: string;
  number: string;
  totalMinor: number;
  balanceMinor: number;
  dueDate?: string;
  validUntil?: string;
  lang: Lang;
}): Record<string, string> {
  const name = (input.customerName || '').trim();
  return {
    customerName: name,
    customerFirstName: name.split(/\s+/)[0] || name,
    shopName: input.shopName,
    shopPhone: input.shopPhone || '',
    documentType: input.docType === 'QUOTATION' ? 'quotation' : 'invoice',
    number: input.number,
    amount: fmtMoney(input.totalMinor, input.lang),
    balance: fmtMoney(Math.max(0, input.balanceMinor), input.lang),
    dueDate: fmtDate(input.dueDate),
    validUntil: fmtDate(input.validUntil),
    link: LINK_PLACEHOLDER,
  };
}

/** Values in a pre-issue text that unfill turns back into variables. */
export function unfillable(vars: Record<string, string>): Record<string, string> {
  const keep = ['number', 'link', 'amount', 'balance', 'dueDate', 'validUntil'];
  return Object.fromEntries(Object.entries(vars).filter(([k, v]) => keep.includes(k) && v));
}
